import type { PreviewComment } from "../api/contract";
import type { UndoState } from "../hooks/useUndo";

export type SimpleChatResultChoice = "undo" | "restore";

/** Completed comment batches can only use the brief Undo offer while they are the newest batch. */
export function resultChoice(comment: PreviewComment, comments: PreviewComment[], undo: UndoState): SimpleChatResultChoice {
  if (undo.phase !== "offered") return "restore";
  const latestBatch = [...comments].reverse().find((candidate) => candidate.state === "done" && candidate.messageId)?.messageId;
  return comment.messageId && comment.messageId === latestBatch ? "undo" : "restore";
}

/** Comments Stax has finished with, whether it made the change, couldn't, or asked: none silently disappears. */
export function resultComments(comments: PreviewComment[]): PreviewComment[] {
  return comments.filter((comment) => comment.state === "done" || comment.state === "failed" || comment.state === "asked");
}

/** A reply's paragraphs: blank lines separate them, and a single line break stays inside one. */
export function replyParagraphs(text: string): string[] {
  return text.split(/\n\s*\n/).map((paragraph) => paragraph.trim()).filter(Boolean);
}

/**
 * How many paragraphs come before the outcome card in a reply: the card follows the first, which
 * says what happened, and so sits before a closing next-step question. A one-paragraph reply has
 * the card after it.
 */
export function cardSlot(paragraphCount: number): number {
  return Math.min(1, paragraphCount);
}

/** Stax is named on its first message after the owner wrote, or at the start, not on every message. */
export function showsStaxLabel(messages: Array<{ role: string }>, index: number): boolean {
  return messages[index]?.role === "admin" && (index === 0 || messages[index - 1]!.role === "owner");
}

export interface ResultPlacement {
  /** Batches whose message isn't in the transcript, shown before it. */
  leading: PreviewComment[][];
  /** Batches by the id of the message they follow: the last reply before the owner next wrote. */
  after: Map<string, PreviewComment[][]>;
}

/**
 * Where each finished comment batch's results sit in the transcript: after the replies to the
 * message that sent it, so they read in order with the conversation.
 */
export function resultPlacement(messages: Array<{ id: string; role: string }>, results: PreviewComment[]): ResultPlacement {
  const batches = new Map<string, PreviewComment[]>();
  for (const comment of results) {
    const key = comment.messageId ?? "";
    batches.set(key, [...(batches.get(key) ?? []), comment]);
  }
  const placement: ResultPlacement = { leading: [], after: new Map() };
  for (const [messageId, batch] of batches) {
    const sent = messageId ? messages.findIndex((message) => message.id === messageId) : -1;
    if (sent < 0) {
      placement.leading.push(batch);
      continue;
    }
    let anchor = sent;
    while (anchor + 1 < messages.length && messages[anchor + 1]!.role !== "owner") anchor++;
    const id = messages[anchor]!.id;
    placement.after.set(id, [...(placement.after.get(id) ?? []), batch]);
  }
  return placement;
}
